import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function Register() {
  const { register, apiError } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    first_name: "", last_name: "", username: "", email: "",
    password: "", birth_date: "", is_adult: false,
    referral_code: params.get("ref") || "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.is_adult) { toast.error("Devi dichiarare di essere maggiorenne."); return; }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Benvenuto su Il Mago Tony, ${u.username}!`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 tw-grain">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-5 py-10">
        <div className="mb-4 flex flex-col items-center">
          <LogoMark className="h-28 w-28 object-contain" />
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-3xl border-2 border-zinc-950 bg-white p-6 shadow-[6px_6px_0_0_#09090b]">
          <h1 className="font-display text-3xl font-black uppercase text-zinc-950">Registrati <span className="text-green-500">gratis</span></h1>
          {form.referral_code && (
            <div data-testid="referral-invited-note" className="rounded-2xl bg-green-100 px-3 py-2 text-sm font-semibold text-green-800">
              Stai entrando su invito di un amico! 🎉 Codice: {form.referral_code}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input data-testid="register-firstname-input" required value={form.first_name} onChange={set("first_name")} className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Cognome</Label>
              <Input data-testid="register-lastname-input" required value={form.last_name} onChange={set("last_name")} className="rounded-xl" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Nickname (pubblico in classifica)</Label>
            <Input data-testid="register-username-input" required minLength={3} value={form.username} onChange={set("username")} placeholder="Es. BomberTony" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Data di nascita</Label>
            <Input data-testid="register-birthdate-input" type="date" required value={form.birth_date} onChange={set("birth_date")} className="rounded-xl" />
          </div>
          <label className="flex items-start gap-3 rounded-2xl bg-slate-100 p-3">
            <Checkbox data-testid="auth-age-gate-checkbox" checked={form.is_adult}
              onCheckedChange={(v) => setForm({ ...form, is_adult: !!v })} className="mt-0.5" />
            <span className="text-sm font-medium text-zinc-700">
              Dichiaro di avere almeno <strong>18 anni</strong>. La registrazione è riservata ai maggiorenni.
            </span>
          </label>
          <Button data-testid="auth-submit-button" type="submit" disabled={loading}
            className="w-full rounded-full bg-zinc-950 py-6 text-base font-black uppercase text-green-400 hover:bg-zinc-800">
            {loading ? "Creazione account..." : "Crea account"}
          </Button>
          <p className="text-center text-sm text-zinc-500">
            Hai già un account? <Link to="/login" className="font-bold text-green-600 hover:underline">Accedi</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
